import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import type { WebAuthnPort } from './types';
import { phoneProof } from './phoneKey';

const AUTH_BASE = import.meta.env.VITE_AUTH_BASE_URL as string;

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${AUTH_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = (await res.json()) as T & { error?: string; message?: string };
  if (!res.ok) throw new Error(json.message ?? json.error ?? `http_${res.status}`);
  return json;
}

/**
 * Client side of the jw-auth flows (Phase 3 backend). The browser prompts
 * for the platform authenticator (Face ID / fingerprint) on both paths.
 * Capacitor swap: native passkey APIs behind the same interface.
 */
export const webauthn: WebAuthnPort = {
  async register(email, deviceLabel) {
    const options = await post<Parameters<typeof startRegistration>[0]['optionsJSON']>(
      '/webauthn/register/options', { email });
    const attestationResponse = await startRegistration({ optionsJSON: options });
    const result = await post<{ ok: boolean; pendingApproval: boolean }>(
      '/webauthn/register/verify', { email, attestationResponse, deviceLabel });
    return { pendingApproval: result.pendingApproval };
  },

  async login(email) {
    const options = await post<
      Parameters<typeof startAuthentication>[0]['optionsJSON']
      & { pendingApproval?: boolean }
    >('/webauthn/login/options', { email });

    /*
     * Stop here if this phone is enrolled but not yet approved.
     *
     * Otherwise the ceremony is started with an empty credential list, the
     * phone opens Face ID, finds nothing it may use, and the browser reports a
     * cancelled prompt — so the driver is told their face failed when the real
     * answer is that the office has not ticked a box. That cost a support call
     * per driver on the first day of onboarding.
     */
    if (options.pendingApproval) {
      throw new Error('device_pending_approval');
    }

    const assertionResponse = await startAuthentication({ optionsJSON: options });
    // This phone's own key signs the same challenge (30 Oct): it is what ties
    // the sign-in to THIS phone, since the passkey is synced to others.
    const phone = await phoneProof(options.challenge).catch(() => null);
    return post('/webauthn/login/verify', { email, assertionResponse, phone });
  },
};
